import swirlBottomLeft from '@assets/images/common/swirl-bottom-left.svg';
import swirlTopRight from '@assets/images/common/swirl-top-right.svg';
import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltLink } from '@cae/cobalt-react/components/link';
import { CobaltLoaderLinear } from '@cae/cobalt-react/components/loader-linear';
import { CobaltLogo } from '@cae/cobalt-react/components/logo';
import { CobaltIconHelpCircleOutline } from '@cae/cobalt-react/icons/outline/CobaltIconHelpCircleOutline';
import { navigateToHelp } from '@shared/helpers/navigate-to-help/navigateToHelp';
import * as React from 'react';

import styles from './AppLoader.module.css';
import { LoaderProps } from './AppLoader.types';
import {
  SLOW_LOADING_MESSAGE,
  SLOW_LOADING_TIMEOUT_MS,
} from './AppLoader.utils';

const AppLoader = React.memo(
  ({
    title = 'Loading your experience...',
    showHelpButton = true,
  }: LoaderProps): React.ReactElement => {
    const [isSlow, setIsSlow] = React.useState(false);

    React.useEffect(() => {
      const timer = globalThis.setTimeout(
        () => setIsSlow(true),
        SLOW_LOADING_TIMEOUT_MS
      );
      return () => globalThis.clearTimeout(timer);
    }, []);

    const handleReload = React.useCallback((): void => {
      globalThis.location.reload();
    }, []);

    return (
      <div className={styles.appLoader} data-testid="app-loader">
        <img
          src={swirlTopRight}
          alt=""
          aria-hidden="true"
          className={styles.swirlTopRight}
        />
        <img
          src={swirlBottomLeft}
          alt=""
          aria-hidden="true"
          className={styles.swirlBottomLeft}
        />

        <header className={styles.header} data-testid="app-loader-header">
          <CobaltLogo size="medium" color="white" />

          {showHelpButton && (
            <CobaltLink variant="inverse" onClick={navigateToHelp}>
              <CobaltIconHelpCircleOutline />
              Help
            </CobaltLink>
          )}
        </header>

        <h1
          className={`${styles.appTitle} heading-05`}
          data-testid="app-loader-app-title"
        >
          CAE Connect
        </h1>

        <div className={styles.content}>
          <CobaltLoaderLinear size="medium" state="loading" />
          <p className="body-01">{title}</p>
          {isSlow && (
            <div
              className={styles.slowLoading}
              role="alert"
              data-testid="app-loader-slow"
            >
              <p className="body-01">{SLOW_LOADING_MESSAGE}</p>
              <CobaltButton
                type="button"
                variant="primary"
                onClick={handleReload}
              >
                Reload page
              </CobaltButton>
            </div>
          )}
        </div>

        <footer
          className={`${styles.footer} body-02`}
          data-testid="app-loader-footer"
        >
          Copyright &copy;{new Date().getFullYear()}, CAE Inc. All rights
          reserved.
        </footer>
      </div>
    );
  }
);

AppLoader.displayName = 'AppLoader';

export { AppLoader };
